import { LitElement, html, css } from 'lit';

import { unsafeHTML } from 'lit/directives/unsafe-html.js';

export class MonacoEditorLit extends LitElement {
  static properties = {
    language: { type: String },
    theme: { type: String, reflect: true },
    code: { type: String },
    loading: { type: Boolean, reflect: true },
    showWatermark: { type: Boolean },
    watermarkText: { type: String },
    watermarkSubText: { type: String },
    readOnly: { type: Boolean, reflect: true },
    autoFormat: { type: Boolean },
  };

  constructor() {
    super();
    this.language = 'javascript';
    this.theme = 'light'; // Use custom Bootstrap-aware theme by default
    //get theme from html attribute
    const theme = this.getAttribute('theme');
    if (theme) {
      if (theme === 'dark' || theme === 'vs-dark') {
        this.theme = 'dark';
      }
    }
    this.watermarkSubText = 'Do not edit manually';
    this.code = '';
    this.loading = false;
    this.showWatermark = false;
    this.watermarkText = 'Autogenerated Code';
    this.readOnly = false;
    this.autoFormat = true;
    this.editor = null;
    this.loadingMessage = 'Loading editor...';
    this.customTypes = [];
    this.errors = [];
    this.cursorPosition = { line: 1, column: 1 };
    this.editorStats = { lines: 0, characters: 0 };
  }

  // Disable shadow DOM
  createRenderRoot() {
    return this;
  }

  // Inject styles into Light DOM
  connectedCallback() {
    super.connectedCallback();
    this._injectStyles();
    document.addEventListener('theme-changed', (e) => {
      this.theme = e.detail.theme;
    });
  }

  _injectStyles() {
    // Check if styles are already injected
    if (document.getElementById('monaco-editor-lit-styles')) return;

    const styleEl = document.createElement('style');
    styleEl.id = 'monaco-editor-lit-styles';
    styleEl.textContent = `
      monaco-editor-lit {
        display: block;
        width: 100%;
        height: 100%;
        position: relative;
        overflow: visible;
        background: var(--bs-body-bg, #1e1e1e);
         border: 1px solid var(--bs-border-color, #444);
      }
      monaco-editor-lit .editor-container {
        width: 100%;
        height: 100%;
        position: relative;
        background: var(--bs-body-bg, #1e1e1e);
      }
      monaco-editor-lit #editor {
        width: 100%;
        height: calc(100% - 26px);
        background: var(--bs-body-bg, #1e1e1e);
      }
      monaco-editor-lit .loader-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.7);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        color: white;
      }
      monaco-editor-lit .loader-spinner {
        border: 4px solid #f3f3f3;
        border-top: 4px solid #3498db;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        animation: monaco-editor-spin 1s linear infinite;
        margin-bottom: 10px;
      }
      @keyframes monaco-editor-spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      monaco-editor-lit .loader-text {
        font-size: 14px;
        margin-top: 10px;
      }
      monaco-editor-lit .watermark {
        position: absolute;
        bottom: 25px;
        right: 25px;
        /* transform: translate(-50%, -50%) rotate(-45deg); */
        font-size: 48px;
        font-weight: bold;
        color: var(--bs-tertiary-color, rgba(255, 255, 255, 0.15));
        pointer-events: none;
        z-index: 1;
        white-space: nowrap;
        user-select: none;
        text-align: right;
      }
        monaco-editor-lit .watermark .sub-text {
        font-size: 16px;
        font-weight: normal;
        color: var(--bs-tertiary-color, rgba(255, 255, 255, 0.15));
      }
      monaco-editor-lit .error-overlay {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        background: rgba(220, 38, 38, 0.95);
        color: white;
        padding: 8px 12px;
        font-size: 12px;
        font-family: monospace;
        z-index: 999;
        max-height: 100px;
        overflow-y: auto;
      }
      monaco-editor-lit .info-bar {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        background: var(--bs-body-bg, #1e1e1e);
        color: var(--bs-body-color, #f8f9fa);
        padding: 4px 12px;
        font-size: 11px;
        font-family: monospace;
        z-index: 998;
        display: flex;
        justify-content: space-between;
        border-top: 1px solid var(--bs-border-color, #444);
      }
      monaco-editor-lit .info-bar.light {
        background: var(--bs-body-bg, #f8f9fa);
        color: var(--bs-body-color, #333);
        border-top: 1px solid var(--bs-border-color, #ccc);
      }
    `;
    document.head.appendChild(styleEl);
  }

  render() {
    return html`
      <div class="editor-container">
        ${this.showWatermark
          ? html`<div class="watermark">
              <div>${unsafeHTML(this.watermarkText)}</div>
              <div class="sub-text">${unsafeHTML(this.watermarkSubText)}</div>
            </div>`
          : ''}
        <div id="editor"></div>
        ${this.loading
          ? html`
              <div class="loader-overlay">
                <div class="loader-spinner"></div>
                <div class="loader-text">${this.loadingMessage}</div>
              </div>
            `
          : ''}
        <div class="info-bar ${this.theme === 'vs-light' ? 'light' : ''}">
          <span>Ln ${this.cursorPosition.line}, Col ${this.cursorPosition.column}</span>
          <span>${this.editorStats.lines} lines | ${this.editorStats.characters} chars | ${this.language.toUpperCase()}</span>
        </div>
      </div>
    `;
  }

  firstUpdated() {
    this._initializeEditor();
  }

  willUpdate(changedProperties) {
    super.willUpdate(changedProperties);

    // If theme property changed and editor exists, update Monaco theme
    if (changedProperties.has('theme') && this.editor) {
      const monacoTheme = this.theme === 'dark' ? 'dark' : 'light';
      window.monaco.editor.setTheme(monacoTheme);
    }
  }

  async _initializeEditor() {
    if (!window.monaco) {
      console.error('Monaco is not loaded!');
      this.loadingMessage = 'Waiting for Editor...';
      this.loading = true;
      setTimeout(() => this._initializeEditor(), 500);
      return;
    }

    this.loading = true;
    this.loadingMessage = 'Initializing editor...';
    this.requestUpdate();

    await this._setupMonaco();
    this.editor = window.monaco.editor.create(this.querySelector('#editor'), {
      value: this.code || this._getDefaultCode(),
      language: this.language,
      theme: this.theme,
      automaticLayout: true,
      minimap: { enabled: true },
      fontSize: 14,
      lineNumbers: 'on',
      roundedSelection: false,
      scrollBeyondLastLine: false,
      readOnly: this.readOnly,
      wordWrap: 'on',
      tabSize: 2,
      insertSpaces: true,
      formatOnPaste: this.autoFormat,
      formatOnType: this.autoFormat,
    });

    // Set up event listeners
    this._setupEventListeners();

    // Initial stats update
    this._updateStats();

    this.loading = false;
    this.requestUpdate();

    // Auto-format on load
    if (this.autoFormat) {
      setTimeout(() => this.formatCode(), 100);
    }

    // Dispatch ready event
    this.dispatchEvent(
      new CustomEvent('editor-ready', {
        detail: { editor: this.editor },
      })
    );
  }

  async _setupMonaco() {
    // Get Bootstrap's body background color
    const bsBodyBg = getComputedStyle(document.body).backgroundColor || '#1e1e1e';

    // Define custom dark theme with Bootstrap colors
    window.monaco.editor.defineTheme('dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [],
      colors: {
        'editor.background': '#212529',
        // 'editor.lineHighlightBackground': '#212529',
      },
    });
    window.monaco.editor.defineTheme('light', {
      base: 'vs',
      inherit: true,
      rules: [],
      colors: {
        'editor.background': '#FFFFFF',
        // 'editor.lineHighlightBackground': '#f8f9fa',
      },
    });

    // Configure TypeScript compiler options
    window.monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
      target: window.monaco.languages.typescript.ScriptTarget.ES2020,
      allowNonTsExtensions: true,
      moduleResolution: window.monaco.languages.typescript.ModuleResolutionKind.NodeJs,
      module: window.monaco.languages.typescript.ModuleKind.CommonJS,
      noEmit: true,
      esModuleInterop: true,
      jsx: window.monaco.languages.typescript.JsxEmit.React,
      allowJs: true,
      typeRoots: ['node_modules/@types'],
    });

    window.monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
      target: window.monaco.languages.typescript.ScriptTarget.ES2020,
      allowNonTsExtensions: true,
      moduleResolution: window.monaco.languages.typescript.ModuleResolutionKind.NodeJs,
      module: window.monaco.languages.typescript.ModuleKind.CommonJS,
      noEmit: true,
      esModuleInterop: true,
      jsx: window.monaco.languages.typescript.JsxEmit.React,
      typeRoots: ['node_modules/@types'],
    });

    // Enable diagnostics
    window.monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: false,
      noSyntaxValidation: false,
    });

    window.monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: false,
      noSyntaxValidation: false,
    });

    // Configure HTML language settings with better IntelliSense
    window.monaco.languages.html.htmlDefaults.setOptions({
      format: {
        tabSize: 2,
        insertSpaces: true,
        wrapLineLength: 120,
        unformatted: 'wbr',
        contentUnformatted: 'pre,code,textarea',
        indentInnerHtml: false,
        preserveNewLines: true,
        maxPreserveNewLines: null,
        indentHandlebars: false,
        endWithNewline: false,
        extraLiners: 'head, body, /html',
        wrapAttributes: 'auto',
      },
      suggest: {
        html5: true,
        angular1: true,
        ionic: true,
      },
      typeRoots: ['node_modules/@types'],
    });

    // Configure CSS language settings with better IntelliSense
    window.monaco.languages.css.cssDefaults.setOptions({
      validate: true,
      lint: {
        compatibleVendorPrefixes: 'warning',
        vendorPrefix: 'warning',
        duplicateProperties: 'warning',
        emptyRules: 'warning',
        importStatement: 'ignore',
        boxModel: 'ignore',
        universalSelector: 'ignore',
        zeroUnits: 'ignore',
        fontFaceProperties: 'warning',
        hexColorLength: 'error',
        argumentsInColorFunction: 'error',
        unknownProperties: 'warning',
        ieHack: 'ignore',
        unknownVendorSpecificProperties: 'ignore',
        propertyIgnoredDueToDisplay: 'warning',
        important: 'ignore',
        float: 'ignore',
        idSelector: 'ignore',
      },
      typeRoots: ['node_modules/@types'],
    });

    // Configure SCSS language settings
    window.monaco.languages.css.scssDefaults.setOptions({
      validate: true,
      lint: {
        compatibleVendorPrefixes: 'warning',
        vendorPrefix: 'warning',
        duplicateProperties: 'warning',
        emptyRules: 'warning',
        importStatement: 'ignore',
        boxModel: 'ignore',
        universalSelector: 'ignore',
        zeroUnits: 'ignore',
        fontFaceProperties: 'warning',
        hexColorLength: 'error',
        argumentsInColorFunction: 'error',
        unknownProperties: 'warning',
        ieHack: 'ignore',
        unknownVendorSpecificProperties: 'ignore',
        propertyIgnoredDueToDisplay: 'warning',
        important: 'ignore',
        float: 'ignore',
        idSelector: 'ignore',
      },
    });

    // Configure LESS language settings
    window.monaco.languages.css.lessDefaults.setOptions({
      validate: true,
      lint: {
        compatibleVendorPrefixes: 'warning',
        vendorPrefix: 'warning',
        duplicateProperties: 'warning',
        emptyRules: 'warning',
        importStatement: 'ignore',
        boxModel: 'ignore',
        universalSelector: 'ignore',
        zeroUnits: 'ignore',
        fontFaceProperties: 'warning',
        hexColorLength: 'error',
        argumentsInColorFunction: 'error',
        unknownProperties: 'warning',
        ieHack: 'ignore',
        unknownVendorSpecificProperties: 'ignore',
        propertyIgnoredDueToDisplay: 'warning',
        important: 'ignore',
        float: 'ignore',
        idSelector: 'ignore',
      },
    });
  }

  _setupEventListeners() {
    if (!this.editor) return;

    // Track cursor position
    this.editor.onDidChangeCursorPosition((e) => {
      this.cursorPosition = {
        line: e.position.lineNumber,
        column: e.position.column,
      };
      this.requestUpdate();
    });

    // Track content changes
    this.editor.onDidChangeModelContent(() => {
      this._updateStats();
      this._checkErrors();
      this.dispatchEvent(
        new CustomEvent('code-changed', {
          detail: { code: this.getCode() },
        })
      );
    });

    // Track model changes (language switches)
    this.editor.onDidChangeModel(() => {
      this._updateStats();
      this._checkErrors();
    });
  }

  _updateStats() {
    if (!this.editor) return;

    const model = this.editor.getModel();
    if (model) {
      this.editorStats.lines = model.getLineCount();
      this.editorStats.characters = model.getValueLength();
      this.requestUpdate();
    }
  }

  async _checkErrors() {
    if (!this.editor) return;

    const model = this.editor.getModel();
    if (!model) return;

    const uri = model.uri;

    // Get markers (errors, warnings, info)
    const markers = window.monaco.editor.getModelMarkers({ resource: uri });

    this.errors = markers.map((marker) => ({
      severity: this._getSeverityName(marker.severity),
      message: marker.message,
      line: marker.startLineNumber,
      column: marker.startColumn,
      endLine: marker.endLineNumber,
      endColumn: marker.endColumn,
      code: marker.code,
    }));

    this.dispatchEvent(
      new CustomEvent('errors-changed', {
        detail: { errors: this.errors },
      })
    );
  }

  _getSeverityName(severity) {
    const severities = {
      1: 'Hint',
      2: 'Info',
      4: 'Warning',
      8: 'Error',
    };
    return severities[severity] || 'Unknown';
  }

  _getDefaultCode() {
    const defaults = {
      javascript: '// JavaScript code\nconsole.log("Hello, World!");',
      typescript: '// TypeScript code\nconst greeting: string = "Hello, World!";\nconsole.log(greeting);',
      html: '<!DOCTYPE html>\n<html>\n<head>\n  <title>Document</title>\n</head>\n<body>\n  <h1>Hello, World!</h1>\n</body>\n</html>',
      css: '/* CSS styles */\nbody {\n  margin: 0;\n  padding: 0;\n  font-family: Arial, sans-serif;\n}',
    };
    return defaults[this.language] || '';
  }

  // ==================== PUBLIC API ====================

  /**
   * Get current code from editor
   */
  getCode() {
    if (!this.editor) return '';
    return this.editor.getValue();
  }

  /**
   * Set code in editor
   */
  setCode(code, language = null) {
    if (!this.editor) {
      this.code = code;
      if (language) this.language = language;
      return;
    }

    if (language && language !== this.language) {
      this.setLanguage(language);
    }

    this.editor.setValue(code);

    if (this.autoFormat) {
      setTimeout(() => this.formatCode(), 100);
    }
  }

  /**
   * Get code for specific language (if TypeScript, can export as JS)
   */
  async getCodeForLanguage(targetLanguage = null) {
    const currentCode = this.getCode();
    const currentLang = this.language;

    if (!targetLanguage) targetLanguage = currentLang;

    // TypeScript to JavaScript conversion
    if (currentLang === 'typescript' && targetLanguage === 'javascript') {
      return await this.transpileTypeScriptToJavaScript();
    }

    return currentCode;
  }

  /**
   * Transpile TypeScript to JavaScript
   */
  async transpileTypeScriptToJavaScript(showLoader = true) {
    if (showLoader) {
      this.loading = true;
      this.loadingMessage = 'Converting TypeScript to JavaScript...';
      this.requestUpdate();
    }

    try {
      const tsCode = this.getCode();

      // Use Monaco's TypeScript service
      const model = this.editor.getModel();
      const uri = model.uri;

      // Get the worker
      const worker = await window.monaco.languages.typescript.getTypeScriptWorker();
      const client = await worker(uri);

      // Get emit output
      const result = await client.getEmitOutput(uri.toString());

      // Extract JavaScript
      let jsCode = '';
      if (result.outputFiles && result.outputFiles.length > 0) {
        jsCode = result.outputFiles[0].text;
      } else {
        // Fallback: use TypeScript compiler if available
        if (window.ts) {
          const compileResult = window.ts.transpileModule(tsCode, {
            compilerOptions: {
              target: window.ts.ScriptTarget.ES2020,
              module: window.ts.ModuleKind.CommonJS,
            },
          });
          jsCode = compileResult.outputText;
        } else {
          throw new Error('TypeScript compiler not available');
        }
      }

      if (showLoader) {
        this.loading = false;
        this.requestUpdate();
      }

      this.dispatchEvent(
        new CustomEvent('transpile-complete', {
          detail: { javascript: jsCode, typescript: tsCode },
        })
      );

      return jsCode;
    } catch (error) {
      console.error('Transpilation error:', error);
      if (showLoader) {
        this.loading = false;
        this.requestUpdate();
      }
      throw error;
    }
  }

  /**
   * Get raw TypeScript code
   */
  getRawTypeScript() {
    if (this.language !== 'typescript') {
      throw new Error('Current language is not TypeScript');
    }
    return this.getCode();
  }

  /**
   * Set language
   */
  setLanguage(language) {
    this.language = language;
    if (this.editor) {
      const model = this.editor.getModel();
      window.monaco.editor.setModelLanguage(model, language);
    }
  }

  /**
   * Set theme
   */
  setTheme(theme) {
    this.theme = theme;
    if (this.editor) {
      window.monaco.editor.setTheme(theme);
    }
    this.requestUpdate();
  }

  /**
   * Toggle theme between dark and light
   */
  toggleTheme() {
    const newTheme = this.theme === 'vs-dark' ? 'vs-light' : 'vs-dark';
    this.setTheme(newTheme);
  }

  /**
   * Format code
   */
  async formatCode() {
    if (!this.editor) return;

    this.loading = true;
    this.loadingMessage = 'Formatting code...';
    this.requestUpdate();

    await this.editor.getAction('editor.action.formatDocument').run();

    this.loading = false;
    this.requestUpdate();
  }

  /**
   * Get cursor position
   */
  getCursorPosition() {
    return { ...this.cursorPosition };
  }

  /**
   * Get editor statistics
   */
  getStats() {
    return { ...this.editorStats };
  }

  /**
   * Get current errors
   */
  getErrors() {
    return [...this.errors];
  }

  /**
   * Add custom type definitions for IntelliSense
   */
  addCustomTypes(definitions) {
    if (typeof definitions === 'string') {
      // Raw .d.ts string
      this._addTypeDefinition(definitions);
    } else if (typeof definitions === 'object') {
      // JSON object - convert to .d.ts
      const dts = this._jsonToTypeDefinition(definitions);
      this._addTypeDefinition(dts);
    }

    this.customTypes.push(definitions);

    this.dispatchEvent(
      new CustomEvent('types-added', {
        detail: { definitions },
      })
    );
  }

  _addTypeDefinition(dtsContent) {
    const libName = `custom-types-${Date.now()}.d.ts`;

    window.monaco.languages.typescript.javascriptDefaults.addExtraLib(dtsContent, `file:///node_modules/@types/${libName}`);

    window.monaco.languages.typescript.typescriptDefaults.addExtraLib(dtsContent, `file:///node_modules/@types/${libName}`);
  }

  _jsonToTypeDefinition(jsonDef) {
    let dts = '';

    // Add global variables
    if (jsonDef.variables) {
      jsonDef.variables.forEach((variable) => {
        dts += `/**\n * ${variable.description || variable.name}\n */\n`;
        dts += `declare var ${variable.name}: ${variable.type || 'any'};\n\n`;
      });
    }

    // Add global constants
    if (jsonDef.constants) {
      jsonDef.constants.forEach((constant) => {
        dts += `/**\n * ${constant.description || constant.name}\n */\n`;
        dts += `declare const ${constant.name}: ${constant.type || 'any'};\n\n`;
      });
    }

    // Define 'this' context
    if (jsonDef.thisContext) {
      dts += `/**\n * ${jsonDef.thisContext.description || 'The this context'}\n */\n`;
      dts += `declare const this: ${jsonDef.thisContext.type};\n\n`;
    }

    if (jsonDef.classes) {
      jsonDef.classes.forEach((cls) => {
        dts += `/**\n * ${cls.description || cls.name}\n */\n`;
        dts += `declare class ${cls.name} {\n`;

        if (cls.constructor) {
          dts += `  /**\n   * ${cls.constructor.description || 'Constructor'}\n`;
          if (cls.constructor.params) {
            cls.constructor.params.forEach((p) => {
              dts += `   * @param ${p.name} - ${p.description || ''}\n`;
            });
          }
          dts += `   */\n`;
          const params = cls.constructor.params ? cls.constructor.params.map((p) => `${p.name}: ${p.type || 'any'}`).join(', ') : '';
          dts += `  constructor(${params});\n\n`;
        }

        if (cls.properties) {
          cls.properties.forEach((prop) => {
            dts += `  /**\n   * ${prop.description || prop.name}\n   */\n`;
            dts += `  ${prop.name}: ${prop.type || 'any'};\n\n`;
          });
        }

        if (cls.methods) {
          cls.methods.forEach((method) => {
            dts += `  /**\n   * ${method.description || method.name}\n`;
            if (method.params) {
              method.params.forEach((p) => {
                dts += `   * @param ${p.name} - ${p.description || ''}\n`;
              });
            }
            if (method.returns) {
              dts += `   * @returns ${method.returns.description || ''}\n`;
            }
            dts += `   */\n`;
            const params = method.params ? method.params.map((p) => `${p.name}: ${p.type || 'any'}`).join(', ') : '';
            const returnType = method.returns?.type || 'void';
            dts += `  ${method.name}(${params}): ${returnType};\n\n`;
          });
        }

        dts += `}\n\n`;
      });
    }

    if (jsonDef.interfaces) {
      jsonDef.interfaces.forEach((iface) => {
        dts += `/**\n * ${iface.description || iface.name}\n */\n`;
        dts += `declare interface ${iface.name} {\n`;

        if (iface.properties) {
          iface.properties.forEach((prop) => {
            dts += `  /**\n   * ${prop.description || prop.name}\n   */\n`;
            dts += `  ${prop.name}${prop.optional ? '?' : ''}: ${prop.type || 'any'};\n\n`;
          });
        }

        dts += `}\n\n`;
      });
    }

    if (jsonDef.functions) {
      jsonDef.functions.forEach((fn) => {
        dts += `/**\n * ${fn.description || fn.name}\n`;
        if (fn.params) {
          fn.params.forEach((p) => {
            dts += ` * @param ${p.name} - ${p.description || ''}\n`;
          });
        }
        if (fn.returns) {
          dts += ` * @returns ${fn.returns.description || ''}\n`;
        }
        dts += ` */\n`;
        const params = fn.params ? fn.params.map((p) => `${p.name}: ${p.type || 'any'}`).join(', ') : '';
        const returnType = fn.returns?.type || 'void';
        dts += `declare function ${fn.name}(${params}): ${returnType};\n\n`;
      });
    }

    return dts;
  }

  /**
   * Clear all custom types
   */
  clearCustomTypes() {
    // Monaco doesn't provide a direct way to remove libs,
    // but we can reset by clearing and re-adding
    this.customTypes = [];

    // Note: This requires reinitializing the defaults
    window.monaco.languages.typescript.javascriptDefaults.setExtraLibs([]);
    window.monaco.languages.typescript.typescriptDefaults.setExtraLibs([]);
  }

  /**
   * Show/hide watermark
   */
  setWatermark(show, text = null, subText = null) {
    this.showWatermark = show;
    if (text) this.watermarkText = text;
    if (subText) this.watermarkSubText = subText;
    this.requestUpdate();
  }

  /**
   * Export code with all metadata
   */
  async exportCode() {
    this.loading = true;
    this.loadingMessage = 'Exporting code...';
    this.requestUpdate();

    const exportData = {
      language: this.language,
      code: this.getCode(),
      errors: this.getErrors(),
      stats: this.getStats(),
      cursorPosition: this.getCursorPosition(),
      timestamp: new Date().toISOString(),
    };

    // Add transpiled JS if TypeScript
    if (this.language === 'typescript') {
      try {
        exportData.javascript = await this.transpileTypeScriptToJavaScript(false);
      } catch (error) {
        exportData.transpileError = error.message;
      }
    }

    this.loading = false;
    this.requestUpdate();

    this.dispatchEvent(
      new CustomEvent('code-exported', {
        detail: exportData,
      })
    );

    return exportData;
  }

  /**
   * Focus the editor
   */
  focus() {
    if (this.editor) {
      this.editor.focus();
    }
  }

  goToMarker(row, rowEnd, column, columnEnd, select = true) {
    let editor = this.editor;
    let monaco = window.monaco;
    if (!editor || !monaco) return;
    if (isNaN(row) || isNaN(column) || isNaN(rowEnd) || isNaN(columnEnd)) return;

    let marker = { line: row, column: column, endLine: rowEnd, endColumn: columnEnd };
    const start = {
      lineNumber: marker.line || marker.startLine || marker.startLineNumber || marker.startLineNumber,
      column: marker.column || marker.startColumn || marker.startColumnNumber || marker.startColumnNumber,
    };
    const end = {
      lineNumber: marker.endLine || marker.endLineNumber || marker.endLineNumber || marker.endLine,
      column: marker.endColumn || marker.endColumnNumber || marker.endColumnNumber || marker.endColumn,
    };

    // create a Range
    const range = new monaco.Range(start.lineNumber, start.column, end.lineNumber, end.column);

    if (select) {
      // select the error and reveal it in center (or use revealRangeNearTop / revealRangeAtTop)
      editor.setSelection(range);
      editor.revealRangeInCenter(range);
    } else {
      editor.setPosition({ lineNumber: rowEnd, column: columnEnd });
      editor.revealPositionInCenter({ lineNumber: rowEnd, column: columnEnd });
    }

    // optionally focus editor so keyboard works
    editor.focus();
  }

  /**
   * Destroy the editor
   */
  destroy() {
    if (this.editor) {
      this.editor.dispose();
      this.editor = null;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.destroy();
  }
}
if (!customElements.get('monaco-editor-lit')) {
  customElements.define('monaco-editor-lit', MonacoEditorLit);
}
